/** Studio: every series with its state, progress and the state of its Claude Code session. */
import { Link, navigate, useLocation } from "../../app/router";
import type { SeriesSummary } from "../../api/types";
import { Icon } from "../../components/Icon";
import { Badge, Chip, EmptyState, PageHeader, Progress, QueryView } from "../../components/ui";
import { useDashboard, useSeriesList } from "../../data/queries";
import { relative } from "../../lib/format";
import { SERIES_STATUS, SESSION_STATE } from "../../lib/status";

const FILTERS: { id: string; label: string; test: (s: SeriesSummary) => boolean }[] = [
  { id: "active", label: "Active", test: (s) => !["complete", "cancelled"].includes(s.status) },
  { id: "attention", label: "Needs you", test: (s) => s.open_decisions > 0 || s.session_state === "waiting" || s.status === "awaiting_selection" },
  { id: "awaiting_selection", label: "Plans", test: (s) => s.status === "awaiting_selection" },
  { id: "building", label: "Building", test: (s) => ["planning", "building"].includes(s.status) },
  { id: "in_review", label: "In review", test: (s) => s.status === "in_review" || s.episodes_in_review > 0 },
  { id: "all", label: "All", test: () => true },
];

export function StudioListScreen() {
  const q = useSeriesList();
  const { search } = useLocation();
  const filter = FILTERS.find((f) => f.id === search.get("filter")) ?? FILTERS[0]!;
  const learnings = useDashboard().data?.learnings_pending ?? 0;
  return (
    <div className="stack-lg">
      <PageHeader title="Studio" subtitle="One Studio conversation per series." actions={<>
        <Link to="/learnings" className="btn btn-secondary btn-md" aria-label={learnings ? `Learnings, ${learnings} to review` : "Learnings"}><Icon name="bulb" size={20} /><span>Learnings</span>{learnings > 0 && <span className="count-pill" aria-hidden>{learnings}</span>}</Link>
        <Link to="/new" className="btn btn-primary btn-md"><Icon name="plus" size={20} /><span>New</span></Link>
      </>} />
      <QueryView q={q}>{(list) => (
        <>
          <div className="chips scroll-x" role="group" aria-label="Filter series">
            {FILTERS.map((f) => (
              <Chip key={f.id} pressed={f.id === filter.id} count={list.filter(f.test).length} onClick={() => navigate(f.id === "active" ? "/studio" : `/studio?filter=${f.id}`, { replace: true })}>{f.label}</Chip>
            ))}
          </div>
          {(() => {
            const items = list.filter(filter.test);
            if (!items.length) return list.length
              ? <EmptyState icon="check" title="Nothing here">No series match this filter.</EmptyState>
              : <EmptyState icon="studio" title="No series yet" action={<Link to="/new" className="btn btn-primary btn-md">Submit your first job</Link>}>Submit a transcript, a YouTube link or a topic and the Studio will propose a plan.</EmptyState>;
            return <ul className="cards">{items.map((s) => <li key={s.id}><SeriesCard s={s} /></li>)}</ul>;
          })()}
        </>
      )}</QueryView>
    </div>
  );
}

export function SeriesCard({ s }: { s: SeriesSummary }) {
  const st = SERIES_STATUS[s.status];
  const ss = SESSION_STATE[s.session_state];
  const done = s.episodes_total ? s.episodes_approved / s.episodes_total : 0;
  return (
    <Link to={`/studio/${s.id}`} className={`series-card ${s.open_decisions ? "has-attention" : ""}`}>
      <span className="series-card-top">
        <Badge tone={st.tone}>{st.label}</Badge>
        {s.priority === "urgent" && <Badge tone="danger">Urgent</Badge>}
        <span className="muted small series-card-cat">{s.category}</span>
      </span>
      <span className="series-card-title">{s.title}</span>
      {s.stage_detail && <span className="series-card-stage">{s.stage_detail}</span>}
      {s.episodes_total > 0 && (
        <span className="series-card-progress">
          <Progress value={done} label={`${s.episodes_approved} of ${s.episodes_total} episodes approved`} tone="ok" />
          <span className="muted small">{s.episodes_approved}/{s.episodes_total} approved{s.episodes_in_review ? ` · ${s.episodes_in_review} in review` : ""}</span>
        </span>
      )}
      <span className="series-card-foot">
        <Badge tone={ss.tone} dot>{ss.label}{s.session_state === "usage_limited" && s.usage_resets_at ? ` · resumes ${relative(s.usage_resets_at)}` : ""}</Badge>
        {s.open_decisions > 0 && <Badge tone="attention">{s.open_decisions} decision{s.open_decisions > 1 ? "s" : ""}</Badge>}
        <span className="muted small push">{relative(s.updated_at)}</span>
      </span>
    </Link>
  );
}
